"use client";

import { useTranslations } from "next-intl";
import NextLink from "next/link";
import { useSearchParams } from "next/navigation";
import React from "react";
import { cn } from "../../helpers/classNames";
import { getRoute } from "../../helpers/urls/getRoute";
import { getUrl } from "../../helpers/urls/getUrl";
import { useCountry } from "../../hooks/useCountry";
import { useViewportInformation } from "../../hooks/useViewportInformation";
import { useDayjs } from "../Dayjs/hooks";
import { Image } from "../Image";
import styles from "./styles.module.scss";

export const WebsiteLogo: React.FC = () => {
    const country = useCountry();
    const dayjs = useDayjs();
    const params = useSearchParams();
    const t = useTranslations("component.websiteLogo");
    const vp = useViewportInformation();
    const logoImage =
        country === "pl"
            ? getUrl({
                  type: "imageAsset",
                  path: `/wetter_com_logo_${country}.svg?20250221-1552`,
              })
            : getUrl({
                  type: "imageAsset",
                  path: "/wetter_com_logo.svg?20221222-1055",
              });

    const today = dayjs();
    const year = dayjs().year();
    const christmas = dayjs(`${year.toString()}-12-24`).startOf("day");
    const afterChristmas = dayjs(`${year.toString()}-12-27`).endOf("day");

    const promotions = {
        christmas:
            today.isBetween(christmas, afterChristmas, "day", "[]") ||
            params.get("promotion") === "christmas",
        pride:
            (today.year() === year && today.month() === 5) ||
            params.get("promotion") === "pride",
    };

    let height = 30;
    let width = 138;
    let pridePromotionHeight = 30;
    let pridePromotionWidth = 64;
    if (vp?.breakpointType === "desktop") {
        height = 35;
        width = 160;
        pridePromotionHeight = 35;
        pridePromotionWidth = 75;
    }

    return (
        <NextLink
            data-label="Header_wetter.com"
            aria-label={t("ariaLabel")}
            title={t("title")}
            href={getRoute({ parameters: { pageName: "home" }, country })}
            className={cn(
                styles.container,
                vp?.breakpointType === "desktop" &&
                    (promotions.pride
                        ? styles.containerDesktopWithPridePromotion
                        : styles.containerDesktop)
            )}
        >
            <Image
                className={styles.wcomLogo}
                src={logoImage}
                width={`${width}px`}
                height={`${height}px`}
                imgTagSize={{ width, height }}
                alt={t("alt")}
                title={t("title")}
                lazy={false}
                preload
            />
            {promotions.christmas && (
                <div
                    className={styles.promotionWrapper}
                    data-testid="christmas-promotion"
                >
                    <Image
                        src={getUrl({
                            type: "imageAsset",
                            path: "/christmas-hat-dark.webp?20231212-1430",
                        })}
                        width="31px"
                        height="23px"
                        imgTagSize={{ width: 31, height: 23 }}
                        alt={t("promotions.christmas")}
                        title={t("promotions.christmas")}
                        lazy={false}
                    />
                </div>
            )}
            {promotions.pride && (
                <Image
                    className={cn(
                        styles.pridePromotion,
                        vp?.breakpointType === "desktop" &&
                            styles.pridePromotionDesktop
                    )}
                    src={getUrl({
                        type: "imageAsset",
                        path: "/logo_pride_addition.svg?20240528-1437",
                    })}
                    width={`${pridePromotionWidth}px`}
                    height={`${pridePromotionHeight}px`}
                    imgTagSize={{
                        width: pridePromotionWidth,
                        height: pridePromotionHeight,
                    }}
                    alt={t("promotions.pride")}
                    title={t("promotions.pride")}
                    lazy={false}
                    data-testid="pride-promotion"
                />
            )}
        </NextLink>
    );
};
